import type { Locale } from '@/context/LangContext'

type L = Record<Locale, string>
type Pair = { title: L; body: L }

export interface HostingPageContent {
  slug: string
  name: string
  meta: { title: L; description: L }
  hero: {
    eyebrow: L
    title: L
    titleAccent: L
    lead: L
    primaryCta: L
    secondaryCta: L
    trust: L[]
    /** Optional deploy-log card shown beside the headline. */
    terminal?: {
      path: string
      branch: string
      caption: L
      lines: { text: string; kind: 'cmd' | 'ok' | 'live' }[]
    }
  }
  toc: { label: L; items: { href: string; title: L }[] }
  answer: { heading: L; paragraphs: L[] }
  requirements: { heading: L; intro: L; items: Pair[] }
  comparison: {
    heading: L
    intro: L
    columns: L[]
    rows: { label: L; values: L[] }[]
    note: L
  }
  deploy: { heading: L; intro: L; steps: Pair[] }
  runtime: { heading: L; intro: L; items: Pair[] }
  pricing: { heading: L; body: L; bullets: L[]; link: L }
  migration: { heading: L; intro: L; steps: Pair[] }
  troubleshooting: { heading: L; intro: L; items: { problem: L; fix: L }[] }
  faq: { heading: L; items: { q: L; a: L }[] }
  related: { heading: L; slugs: string[] }
  cta: { heading: L; body: L; button: L }
}

export const hostingHub = {
  meta: {
    title: {
      fa: 'هاست ابری برای هر فریم‌ورک | لکسویا',
      en: 'Cloud hosting for every framework | Lexoya',
      ar: 'استضافة سحابية لكل إطار عمل | لكسويا',
    },
    description: {
      fa: 'لاراول، جنگو، نکست جی اس، نود جی اس و داکر را از ریپازیتوری دیپلوی کنید؛ بدون مدیریت سرور، با دیپلوی خودکار و SSL رایگان. راهنمای هر فریم‌ورک را ببینید.',
      en: 'Deploy Laravel, Django, Next.js, Node.js or Docker straight from your repository. No server management, automatic deploys and free SSL. Pick your framework.',
      ar: 'انشر Laravel أو Django أو Next.js أو Node.js أو Docker من مستودعك مباشرة، دون إدارة خوادم، مع نشر تلقائي وSSL مجاني.',
    },
  },
  labels: {
    home: { fa: 'خانه', en: 'Home', ar: 'الرئيسية' },
    hub: { fa: 'هاست ابری', en: 'Cloud hosting', ar: 'الاستضافة السحابية' },
    viewAll: { fa: 'همه‌ی فریم‌ورک‌ها', en: 'All frameworks', ar: 'كل أطر العمل' },
    cardCta: { fa: 'دیدن راهنما', en: 'Read the guide', ar: 'اطّلع على الدليل' },
  },
  hero: {
    eyebrow: { fa: 'میزبانی اپلیکیشن', en: 'Application hosting', ar: 'استضافة التطبيقات' },
    title: { fa: 'هاست ابری', en: 'Cloud hosting', ar: 'استضافة سحابية' },
    titleAccent: {
      fa: 'برای هر فریم‌ورک',
      en: 'for every framework',
      ar: 'لكل إطار عمل',
    },
    lead: {
      fa: 'ریپازیتوری را وصل می‌کنید و پروژه بالا می‌آید. فریم‌ورک را انتخاب کنید و ببینید برای اجرای درستش چه چیزی لازم است؛ از داکیومنت روت تا صف و زمان‌بند.',
      en: 'Connect the repository and the app goes live. Pick a framework to see what it actually needs to run, from the document root to queues and schedulers.',
      ar: 'اربط المستودع ويُنشر التطبيق. اختر إطار العمل لتعرف ما يحتاجه فعلاً، من جذر المستند إلى الطوابير والمجدول.',
    },
    primaryCta: { fa: 'شروع دیپلوی', en: 'Start deploying', ar: 'ابدأ النشر' },
    secondaryCta: { fa: 'محاسبه‌ی هزینه', en: 'Estimate cost', ar: 'احسب التكلفة' },
    trust: [
      { fa: 'SSL رایگان', en: 'Free SSL', ar: 'SSL مجاني' },
      { fa: 'پرداخت ساعتی', en: 'Hourly billing', ar: 'فوترة ساعية' },
      { fa: 'خوشه‌ی ایران و آلمان', en: 'Iran and Germany clusters', ar: 'مجموعتا إيران وألمانيا' },
    ],
  },
  grid: {
    heading: {
      fa: 'فریم‌ورک‌های پشتیبانی‌شده',
      en: 'Supported frameworks',
      ar: 'أطر العمل المدعومة',
    },
    description: {
      fa: 'برای هر فریم‌ورک یک راهنمای جدا نوشته‌ایم: پیش‌نیازها، مراحل دیپلوی و خطاهایی که سر راه می‌خورید.',
      en: 'Every framework gets its own guide: requirements, deploy steps and the errors you will actually hit.',
      ar: 'لكل إطار عمل دليل خاص: المتطلبات وخطوات النشر والأخطاء التي ستواجهها.',
    },
    soon: { fa: 'به‌زودی', en: 'Soon', ar: 'قريبًا' },
    stackCaption: {
      fa: '۱۲ فریم‌ورک، یک پلتفرم',
      en: '12 frameworks, one platform',
      ar: '١٢ إطار عمل، منصة واحدة',
    },
  },
  how: {
    heading: { fa: 'سه قدم تا اجرا', en: 'Three steps to a running app', ar: 'ثلاث خطوات للتشغيل' },
    items: [
      {
        title: { fa: 'ریپازیتوری را وصل کنید', en: 'Connect the repository', ar: 'اربط المستودع' },
        body: {
          fa: 'گیت‌هاب، گیت‌لب یا گیتیا. شاخه را انتخاب می‌کنید و هر push یک دیپلوی تازه می‌سازد.',
          en: 'GitHub, GitLab or Gitea. Pick the branch; every push builds a new release.',
          ar: 'GitHub أو GitLab أو Gitea. اختر الفرع، وكل push ينشئ إصدارًا جديدًا.',
        },
      },
      {
        title: { fa: 'متغیرها را وارد کنید', en: 'Set the environment', ar: 'اضبط المتغيرات' },
        body: {
          fa: 'کلیدها و رمزها در پنل می‌مانند. فایل .env در ریپازیتوری لازم نیست.',
          en: 'Keys and secrets stay in the panel, not in the repository.',
          ar: 'تبقى المفاتيح وكلمات السر في لوحة التحكم، لا في المستودع.',
        },
      },
      {
        title: { fa: 'دیپلوی کنید', en: 'Deploy', ar: 'انشر' },
        body: {
          fa: 'بیلد، نصب وابستگی و ریلیز انجام می‌شود. لاگ هر مرحله باز است.',
          en: 'Build, dependency install and release run for you. Every stage has a log.',
          ar: 'يُبنى الإصدار وتُثبَّت التبعيات ويُنشر. سجل كل مرحلة متاح.',
        },
      },
    ],
  },
  cta: {
    heading: { fa: 'زیرساخت را بسپارید، کد را بنویسید', en: 'Hand over the infrastructure, keep the code', ar: 'سلّم البنية التحتية واحتفظ بالكود' },
    body: {
      fa: 'هر پروژه یک ساب‌دامین رایگان با SSL می‌گیرد. هزینه بر اساس مصرف حساب می‌شود، ساعتی.',
      en: 'Every project gets a free HTTPS subdomain. Billing is hourly and usage-based.',
      ar: 'كل مشروع يحصل على نطاق فرعي مجاني مع SSL. الفوترة ساعية حسب الاستخدام.',
    },
    button: { fa: 'شروع کنید', en: 'Get started', ar: 'ابدأ الآن' },
  },
}

export const hostingPages: Record<string, HostingPageContent> = {
  laravel: {
    slug: 'laravel',
    name: 'Laravel',
    meta: {
      title: {
        fa: 'هاست لاراول با دیپلوی خودکار و SSL رایگان | لکسویا',
        en: 'Laravel Hosting with Git Deploys and Free SSL | Lexoya',
        ar: 'استضافة Laravel مع نشر تلقائي وSSL مجاني | لكسويا',
      },
      description: {
        fa: 'هاست لاراول بدون محدودیت هاست اشتراکی: دیپلوی از گیت، ریشه‌ی public، صف و زمان‌بند، دیتابیس مدیریت‌شده و SSL رایگان. هزینه را شفاف ببینید.',
        en: 'Laravel hosting without shared-hosting limits: git deploys, a public document root, queue workers, scheduler, managed database and free SSL.',
        ar: 'استضافة Laravel بلا قيود الاستضافة المشتركة: نشر من Git وجذر public وطوابير ومجدول وقاعدة بيانات مُدارة وSSL مجاني.',
      },
    },
    hero: {
      eyebrow: { fa: 'میزبانی لاراول', en: 'Laravel hosting', ar: 'استضافة Laravel' },
      title: { fa: 'هاست لاراول', en: 'Laravel hosting', ar: 'استضافة Laravel' },
      titleAccent: {
        fa: 'بدون محدودیت هاست اشتراکی',
        en: 'beyond shared hosting',
        ar: 'بلا قيود الاستضافة المشتركة',
      },
      lead: {
        fa: 'ریپازیتوری را وصل می‌کنید، متغیرها را وارد می‌کنید و پروژه بالا می‌آید. صف و زمان‌بند بدون تنظیم دستی اجرا می‌شوند و برای نصب وابستگی‌ها لازم نیست تیکت بزنید.',
        en: 'Connect the repository, set the environment and the app goes live. Queues and the scheduler run too, with no hand-made cron and no ticket for every composer install.',
        ar: 'اربط المستودع واضبط المتغيرات ليُنشر التطبيق. تعمل الطوابير والمجدول أيضًا، دون cron يدوي ودون تذكرة لكل composer install.',
      },
      primaryCta: { fa: 'شروع دیپلوی لاراول', en: 'Start a Laravel deploy', ar: 'ابدأ نشر Laravel' },
      secondaryCta: { fa: 'محاسبه‌ی هزینه', en: 'Estimate cost', ar: 'احسب التكلفة' },
      trust: [
        { fa: 'SSL رایگان', en: 'Free SSL', ar: 'SSL مجاني' },
        { fa: 'پرداخت ساعتی', en: 'Hourly billing', ar: 'فوترة ساعية' },
        { fa: 'خوشه‌ی ایران و آلمان', en: 'Iran and Germany clusters', ar: 'مجموعتا إيران وألمانيا' },
        { fa: 'ساب‌دامین رایگان', en: 'Free subdomain', ar: 'نطاق فرعي مجاني' },
      ],
      terminal: {
        path: 'lexoya / laravel-shop',
        branch: 'main',
        caption: {
          fa: 'همین شش خط کل دیپلوی است؛ نه اتصال به سرور، نه تنظیم زمان‌بند.',
          en: 'Those six lines are the whole deploy: no SSH, no hand-made cron.',
          ar: 'هذه الأسطر الستة هي النشر كله: بلا SSH وبلا cron يدوي.',
        },
        lines: [
          { text: 'git push origin main', kind: 'cmd' },
          { text: 'repository cloned', kind: 'ok' },
          { text: 'composer install', kind: 'ok' },
          { text: 'npm run build', kind: 'ok' },
          { text: 'php artisan migrate', kind: 'ok' },
          { text: 'live  your-project.lexoyacloud.ir', kind: 'live' },
        ],
      },
    },
    toc: {
      label: { fa: 'در این صفحه', en: 'On this page', ar: 'في هذه الصفحة' },
      items: [
        { href: '#answer', title: { fa: 'هاست لاراول چیست', en: 'What Laravel hosting is', ar: 'ما هي استضافة Laravel' } },
        { href: '#requirements', title: { fa: 'پیش‌نیازهای پروژه', en: 'What the project needs', ar: 'ما يحتاجه المشروع' } },
        { href: '#comparison', title: { fa: 'مقایسه‌ی سه گزینه', en: 'The three options', ar: 'الخيارات الثلاثة' } },
        { href: '#deploy', title: { fa: 'مراحل دیپلوی', en: 'Deploy steps', ar: 'خطوات النشر' } },
        { href: '#runtime', title: { fa: 'صف و زمان‌بند', en: 'Queues and scheduler', ar: 'الطوابير والمجدول' } },
        { href: '#troubleshooting', title: { fa: 'خطاهای رایج', en: 'Common errors', ar: 'الأخطاء الشائعة' } },
        { href: '#faq', title: { fa: 'پرسش‌های پرتکرار', en: 'Frequent questions', ar: 'الأسئلة الشائعة' } },
      ],
    },
    answer: {
      heading: { fa: 'هاست لاراول چیست؟', en: 'What is Laravel hosting?', ar: 'ما هي استضافة Laravel؟' },
      paragraphs: [
        {
          fa: 'هاست لاراول میزبانی‌ای است که پیش‌نیازهای فریم‌ورک را از پیش آماده دارد: نسخه‌ی مناسب PHP، دسترسی به Composer و artisan، داکیومنت روت روی پوشه‌ی public و امکان اجرای پردازش‌های پس‌زمینه مثل صف و زمان‌بند. تفاوت اصلی‌اش با هاست اشتراکی همین است: در هاست اشتراکی PHP فقط به‌اندازه‌ی یک درخواست وب زنده می‌ماند و ورکر همیشه‌روشن جایی برای اجرا ندارد.',
          en: 'Laravel hosting is hosting that comes with the framework prerequisites already in place: the right PHP version, Composer and artisan, the document root on public, and a way to run background work such as queues and the scheduler. That is the real difference from shared hosting: there PHP lives only as long as one web request, and an always-on worker has nowhere to run.',
          ar: 'استضافة Laravel هي استضافة تجهز متطلبات إطار العمل مسبقًا: نسخة PHP المناسبة وComposer وartisan وجذر المستند على public وإمكانية تشغيل الأعمال الخلفية كالطوابير والمجدول. وهنا الفرق الحقيقي عن الاستضافة المشتركة: هناك لا تعيش PHP إلا مدة طلب واحد، ولا مكان لعامل دائم.',
        },
        {
          fa: 'پروژه‌ی لاراولی روی سه نوع زیرساخت اجرا می‌شود: هاست اشتراکی برای سایت‌های کم‌ترافیک، سرور مجازی برای کنترل کامل، به بهای وقت نگهداری، و پلتفرم ابری که دیپلوی، مقیاس و پردازش پس‌زمینه را بدون مدیریت سرور تحویل می‌دهد. انتخاب درست به این بستگی دارد که پروژه‌تان صف و ورکر دائمی دارد یا نه.',
          en: 'A Laravel project runs on three kinds of infrastructure: shared hosting for low-traffic sites, a VPS for full control at the cost of maintenance, and a cloud platform that delivers deploys, scaling and background processing without server management. The right choice depends on whether the project needs a permanent queue worker.',
          ar: 'يعمل مشروع Laravel على ثلاثة أنواع من البنية: استضافة مشتركة للمواقع قليلة الزيارات، وخادم افتراضي للتحكم الكامل مقابل تكلفة الصيانة، ومنصة سحابية تقدم النشر والتوسع والمعالجة الخلفية دون إدارة خوادم. يعتمد الاختيار على حاجة المشروع إلى عامل طوابير دائم.',
        },
      ],
    },
    requirements: {
      heading: {
        fa: 'پروژه‌ی لاراول از هاست چه می‌خواهد؟',
        en: 'What a Laravel project actually needs',
        ar: 'ما يحتاجه مشروع Laravel فعلاً',
      },
      intro: {
        fa: 'این فهرست معیار خرید است. اگر سرویس‌دهنده‌ای یکی از این‌ها را نداشته باشد، مشکل بعداً به شکل خطا سر می‌زند، نه در صفحه‌ی فروش.',
        en: 'This is the buying checklist. If a provider misses one item, the problem shows up later as an error, not on the sales page.',
        ar: 'هذه قائمة الشراء. إن غاب بند منها عند مزوّد، سيظهر لاحقًا كخطأ لا في صفحة البيع.',
      },
      items: [
        {
          title: { fa: 'نسخه‌ی PHP و افزونه‌ها', en: 'PHP version and extensions', ar: 'نسخة PHP والإضافات' },
          body: {
            fa: 'نسخه‌های جدید لاراول به PHP ۸٫۲ یا بالاتر نیاز دارند. افزونه‌هایی مثل PDO، mbstring، openssl، intl، gd و zip باید فعال باشند و نسخه قابل تغییر باشد، نه قفل‌شده.',
            en: 'Laravel 11 needs PHP 8.2 or newer. PDO, mbstring, openssl, intl, gd and zip must be enabled, and the version must be changeable, not pinned.',
            ar: 'يحتاج Laravel 11 إلى PHP 8.2 أو أحدث، مع تفعيل PDO وmbstring وopenssl وintl وgd وzip، وإمكانية تغيير النسخة لا تثبيتها.',
          },
        },
        {
          title: { fa: 'داکیومنت روت روی public', en: 'Document root on public', ar: 'جذر المستند على public' },
          body: {
            fa: 'اگر دامنه به روت پروژه اشاره کند، فایل .env و سورس از بیرون خواندنی می‌شود. داکیومنت روت باید پوشه‌ی public باشد.',
            en: 'If the domain points at the project root, .env and the source become readable from outside. The document root has to be the public folder.',
            ar: 'إذا أشار النطاق إلى جذر المشروع صار .env والكود مقروءًا من الخارج. يجب أن يكون جذر المستند مجلد public.',
          },
        },
        {
          title: { fa: 'ابزار ساخت: Composer و artisan', en: 'Build tooling: Composer and artisan', ar: 'أدوات البناء: Composer وartisan' },
          body: {
            fa: 'نصب وابستگی و اجرای migrate و config:cache باید بدون تیکت و در زمان دیپلوی انجام شود.',
            en: 'Dependency install and commands such as migrate and config:cache should run at deploy time, without a ticket.',
            ar: 'يجب أن يتم تثبيت التبعيات وتشغيل migrate وconfig:cache وقت النشر، دون تذكرة.',
          },
        },
        {
          title: { fa: 'مرحله‌ی build برای فرانت', en: 'A front-end build step', ar: 'خطوة بناء للواجهة' },
          body: {
            fa: 'اگر Vite یا Mix دارید، جایی برای npm ci و npm run build لازم است؛ وگرنه استایل‌ها روی سرور ساخته نمی‌شوند.',
            en: 'With Vite or Mix you need a place to run npm ci and npm run build, or the styles are never compiled.',
            ar: 'مع Vite أو Mix تحتاج مكانًا لتشغيل npm ci وnpm run build، وإلا لم تُبنَ الأنماط.',
          },
        },
        {
          title: { fa: 'ورکر همیشه‌روشن', en: 'An always-on worker', ar: 'عامل دائم' },
          body: {
            fa: 'صف از یک فرایند دائمی استفاده می‌کند. اگر ورکر بالا نباشد، job‌ها در صف می‌مانند و کاربر پشت صفحه‌ی انتظار گیر می‌کند.',
            en: 'Queues rely on a long-running process. Without a worker, jobs sit in the queue and the user waits on a screen that never finishes.',
            ar: 'تعتمد الطوابير على عملية طويلة الأمد. بدون عامل تبقى المهام في الطابور وينتظر المستخدم بلا نهاية.',
          },
        },
        {
          title: { fa: 'زمان‌بند دقیقه‌ای', en: 'A per-minute scheduler', ar: 'مجدول كل دقيقة' },
          body: {
            fa: 'schedule:run باید هر دقیقه اجرا شود. cron محدود هاست اشتراکی معمولاً همین‌جا شکست می‌خورد.',
            en: 'schedule:run has to fire every minute. The limited cron of shared hosting usually breaks right here.',
            ar: 'يجب أن يعمل schedule:run كل دقيقة، وهنا عادةً يفشل cron المحدود في الاستضافة المشتركة.',
          },
        },
        {
          title: { fa: 'کش و صف روی Redis', en: 'Cache and queue on Redis', ar: 'التخزين المؤقت والطوابير على Redis' },
          body: {
            fa: 'کش فایل و صف دیتابیس تا حدی جواب می‌دهند. با ترافیک واقعی، Redis تفاوت را نشان می‌دهد.',
            en: 'File cache and a database queue work up to a point. Under real traffic, Redis is where the difference shows.',
            ar: 'يعمل التخزين على الملفات والطابور على قاعدة البيانات إلى حدّ ما. ومع حركة حقيقية يظهر فرق Redis.',
          },
        },
        {
          title: { fa: 'ذخیره‌سازی بیرون از سرور', en: 'Storage outside the server', ar: 'تخزين خارج الخادم' },
          body: {
            fa: 'فایل کاربران روی دیسک محلی کانتینر با هر دیپلوی پاک می‌شود؛ باید روی S3 باشد.',
            en: 'User uploads on a container’s local disk vanish with the next deploy; they belong on S3.',
            ar: 'ملفات المستخدمين على قرص الحاوية المحلي تختفي مع كل نشر، فمكانها S3.',
          },
        },
      ],
    },
    comparison: {
      heading: {
        fa: 'هاست اشتراکی، سرور مجازی یا پلتفرم ابری؟',
        en: 'Shared hosting, VPS or a cloud platform?',
        ar: 'استضافة مشتركة أم خادم افتراضي أم منصة سحابية؟',
      },
      intro: {
        fa: 'هیچ‌کدام مطلقاً بهتر نیستند. جدول را بر اساس کاری که پروژه‌تان انجام می‌دهد بخوانید.',
        en: 'None of them is simply better. Read the table against what your project actually does.',
        ar: 'لا أحدها أفضل بإطلاق. اقرأ الجدول بحسب ما يفعله مشروعك فعلاً.',
      },
      columns: [
        { fa: 'هاست اشتراکی', en: 'Shared hosting', ar: 'استضافة مشتركة' },
        { fa: 'سرور مجازی', en: 'VPS', ar: 'خادم افتراضي' },
        { fa: 'پلتفرم ابری لکسویا', en: 'Lexoya cloud platform', ar: 'منصة لكسويا السحابية' },
      ],
      rows: [
        {
          label: { fa: 'منابع', en: 'Resources', ar: 'الموارد' },
          values: [
            { fa: 'مشترک با ده‌ها کاربر دیگر', en: 'Shared with dozens of others', ar: 'مشتركة مع عشرات المستخدمين' },
            { fa: 'اختصاصی، با مدیریت خودتان', en: 'Dedicated, managed by you', ar: 'مخصصة تديرها بنفسك' },
            { fa: 'اختصاصی برای هر پروژه', en: 'Dedicated per project', ar: 'مخصصة لكل مشروع' },
          ],
        },
        {
          label: { fa: 'صف و ورکر', en: 'Queues and workers', ar: 'الطوابير والعوامل' },
          values: [
            { fa: 'معمولاً ندارد یا محدود است', en: 'Usually absent or capped', ar: 'غائبة عادةً أو محدودة' },
            { fa: 'با Supervisor، خودتان تنظیم می‌کنید', en: 'Supervisor, set up by you', ar: 'بـSupervisor، تعدّه بنفسك' },
            { fa: 'پروژه‌ی جدا برای ورکر', en: 'A separate worker project', ar: 'مشروع منفصل للعامل' },
          ],
        },
        {
          label: { fa: 'زمان‌بند', en: 'Scheduler', ar: 'المجدول' },
          values: [
            { fa: 'cron محدود', en: 'Limited cron', ar: 'cron محدود' },
            { fa: 'cron کامل', en: 'Full cron', ar: 'cron كامل' },
            { fa: 'فراخوانی هر دقیقه', en: 'Called every minute', ar: 'استدعاء كل دقيقة' },
          ],
        },
        {
          label: { fa: 'دیپلوی', en: 'Deployment', ar: 'النشر' },
          values: [
            { fa: 'آپلود دستی یا Git روی هاست', en: 'Manual upload or Git on the box', ar: 'رفع يدوي أو Git على الخادم' },
            { fa: 'خودتان با اسکریپت', en: 'Your own scripts', ar: 'سكربتات خاصة بك' },
            { fa: 'push به ریپازیتوری و build خودکار', en: 'Push to git, automatic build', ar: 'push إلى المستودع وبناء تلقائي' },
          ],
        },
        {
          label: { fa: 'نگهداری و امنیت', en: 'Maintenance and security', ar: 'الصيانة والأمان' },
          values: [
            { fa: 'با سرویس‌دهنده', en: 'With the provider', ar: 'عند المزوّد' },
            { fa: 'با شما', en: 'With you', ar: 'عندك' },
            { fa: 'با ما', en: 'With us', ar: 'عندنا' },
          ],
        },
        {
          label: { fa: 'هزینه', en: 'Cost', ar: 'التكلفة' },
          values: [
            { fa: 'کم و ثابت', en: 'Low and flat', ar: 'منخفضة وثابتة' },
            { fa: 'کم، ولی وقت شما گران است', en: 'Low, but your time is not', ar: 'منخفضة، لكن وقتك ليس كذلك' },
            { fa: 'مصرفی و ساعتی', en: 'Usage-based, hourly', ar: 'حسب الاستخدام، بالساعة' },
          ],
        },
      ],
      note: {
        fa: 'برای یک سایت شرکتی ساده با چند صد بازدید روزانه، هاست اشتراکی هنوز ارزان‌ترین انتخاب است. اگر صف، ورکر یا دیپلوی مکرر دارید، هزینه‌ی نگهداری سرور مجازی از اختلاف قیمت بیشتر می‌شود.',
        en: 'For a simple company site with a few hundred daily visits, shared hosting is still the cheapest option. Once you have queues, workers or frequent deploys, the maintenance cost of a VPS outweighs the price difference.',
        ar: 'لموقع شركة بسيط ببضع مئات من الزيارات يوميًا، تبقى الاستضافة المشتركة الأرخص. ومع الطوابير والعوامل والنشر المتكرر يتجاوز وقت صيانة الخادم فرق السعر.',
      },
    },
    deploy: {
      heading: { fa: 'دیپلوی لاراول روی لکسویا', en: 'Deploying Laravel on Lexoya', ar: 'نشر Laravel على لكسويا' },
      intro: {
        fa: 'شش قدم، از ریپازیتوری تا دامنه. هیچ‌کدام نیاز به SSH ندارد.',
        en: 'Six steps from repository to domain. None of them needs SSH.',
        ar: 'ست خطوات من المستودع إلى النطاق، دون حاجة إلى SSH.',
      },
      steps: [
        {
          title: { fa: 'اتصال ریپازیتوری', en: 'Connect the repository', ar: 'اربط المستودع' },
          body: {
            fa: 'گیت‌هاب، گیت‌لب یا گیتیا را وصل کنید و شاخه‌ی اصلی را انتخاب کنید.',
            en: 'Connect GitHub, GitLab or Gitea and pick the main branch.',
            ar: 'اربط GitHub أو GitLab أو Gitea واختر الفرع الرئيسي.',
          },
        },
        {
          title: { fa: 'انتخاب نوع پروژه', en: 'Pick the project type', ar: 'اختر نوع المشروع' },
          body: {
            fa: 'نوع لاراول را انتخاب کنید. اگر ریپازیتوری Dockerfile دارد، مسیرش را بدهید؛ وگرنه قالب آماده‌ی لاراول استفاده می‌شود.',
            en: 'Choose the Laravel type. If the repo has a Dockerfile, point at its path; otherwise the built-in Laravel template is used.',
            ar: 'اختر نوع Laravel. إن كان للمستودع Dockerfile فحدّد مساره، وإلا استُخدم القالب الجاهز.',
          },
        },
        {
          title: { fa: 'متغیرهای محیطی', en: 'Environment variables', ar: 'متغيرات البيئة' },
          body: {
            fa: 'APP_KEY، اتصال دیتابیس و QUEUE_CONNECTION را وارد کنید. رمزها در پنل می‌مانند، نه در ریپازیتوری.',
            en: 'Set APP_KEY, the database connection and QUEUE_CONNECTION. Secrets stay in the panel, not in the repository.',
            ar: 'أدخل APP_KEY واتصال قاعدة البيانات وQUEUE_CONNECTION. تبقى الأسرار في اللوحة لا في المستودع.',
          },
        },
        {
          title: { fa: 'دیپلوی', en: 'Deploy', ar: 'انشر' },
          body: {
            fa: 'بیلد، نصب وابستگی و ریلیز انجام می‌شود. لاگ هر مرحله باز است و مرحله‌ی شکست‌خورده را نشان می‌دهد.',
            en: 'Build, dependency install and release run for you. Every stage has a log, and a failed stage says why.',
            ar: 'يُبنى الإصدار وتُثبَّت التبعيات ويُنشر. سجل كل مرحلة متاح ويبيّن المرحلة الفاشلة.',
          },
        },
        {
          title: { fa: 'ورکر صف', en: 'Queue worker', ar: 'عامل الطابور' },
          body: {
            fa: 'یک پروژه‌ی Supervisor با دستور php artisan queue:work بسازید تا صف جدا از ترافیک وب پردازش شود.',
            en: 'Create a Supervisor project running php artisan queue:work so jobs are processed apart from web traffic.',
            ar: 'أنشئ مشروع Supervisor يشغّل php artisan queue:work لتعالج المهام بعيدًا عن حركة الويب.',
          },
        },
        {
          title: { fa: 'دامنه و SSL', en: 'Domain and SSL', ar: 'النطاق وSSL' },
          body: {
            fa: 'دامنه‌ی اختصاصی را وصل کنید یا از ساب‌دامین رایگان استفاده کنید؛ گواهی SSL خودکار صادر و تمدید می‌شود.',
            en: 'Attach your domain or use the free subdomain; the SSL certificate is issued and renewed automatically.',
            ar: 'اربط نطاقك أو استخدم النطاق الفرعي المجاني؛ تُصدر شهادة SSL وتُجدَّد تلقائيًا.',
          },
        },
      ],
    },
    runtime: {
      heading: {
        fa: 'صف، زمان‌بند و پردازش پس‌زمینه',
        en: 'Queues, scheduler and background work',
        ar: 'الطوابير والمجدول والمعالجة الخلفية',
      },
      intro: {
        fa: 'پنج تنظیمی که تفاوت بین «روی سرور بالا آمد» و «در تولید کار می‌کند» را می‌سازد.',
        en: 'Five settings that separate “it starts on the server” from “it works in production”.',
        ar: 'خمسة إعدادات تفصل بين «يعمل على الخادم» و«يعمل في الإنتاج».',
      },
      items: [
        {
          title: { fa: 'صف روی Redis', en: 'Queues on Redis', ar: 'الطوابير على Redis' },
          body: {
            fa: 'درایور صف را Redis بگذارید و ورکر را جدا اجرا کنید. jobهای شکست‌خورده باید retry و لاگ داشته باشند.',
            en: 'Point the queue driver at Redis and run the worker separately. Failed jobs need retries and logs.',
            ar: 'اجعل مشغّل الطابور Redis وشغّل العامل منفصلًا. تحتاج المهام الفاشلة إلى إعادة محاولة وسجلات.',
          },
        },
        {
          title: { fa: 'زمان‌بند', en: 'Scheduler', ar: 'المجدول' },
          body: {
            fa: 'schedule:run هر دقیقه فراخوانی می‌شود و kernel.php شما را اجرا می‌کند؛ نیازی به تعریف cron در پنل نیست.',
            en: 'schedule:run fires every minute and runs your kernel; there is no cron entry to define in a panel.',
            ar: 'يُستدعى schedule:run كل دقيقة وينفّذ kernel الخاص بك؛ لا حاجة لتعريف cron في لوحة.',
          },
        },
        {
          title: { fa: 'وب‌سوکت با Reverb', en: 'WebSockets with Reverb', ar: 'WebSocket مع Reverb' },
          body: {
            fa: 'Reverb پورت و دامنه‌ی جدا می‌گیرد و کنار پروژه‌ی اصلی اجرا می‌شود. برای بار سنگین، ورکر و وب‌سوکت را از هم جدا نگه دارید.',
            en: 'Reverb takes its own port and domain and runs beside the main app. For heavy loads keep the worker and the socket server apart.',
            ar: 'يأخذ Reverb منفذًا ونطاقًا خاصًا ويعمل إلى جانب التطبيق. للأحمال الثقيلة افصل العامل عن خادم المقبس.',
          },
        },
        {
          title: { fa: 'داشبورد Horizon', en: 'Horizon dashboard', ar: 'لوحة Horizon' },
          body: {
            fa: 'اگر روی Redis کار می‌کنید، Horizon هم بالا می‌آید. مسیرش را با احراز هویت محافظت کنید.',
            en: 'If you run on Redis, Horizon comes up too. Put authentication in front of its route.',
            ar: 'إن عملت على Redis فعّل Horizon أيضًا، واحمِ مساره بالمصادقة.',
          },
        },
        {
          title: { fa: 'فایل کاربران روی S3', en: 'User files on S3', ar: 'ملفات المستخدمين على S3' },
          body: {
            fa: 'FILESYSTEM_DISK=s3 تا آپلودها با هر دیپلوی از بین نروند و بین نسخه‌ها مشترک بمانند.',
            en: 'Set FILESYSTEM_DISK=s3 so uploads survive every deploy and stay shared across releases.',
            ar: 'اضبط FILESYSTEM_DISK=s3 لتبقى الملفات بعد كل نشر ومشتركة بين الإصدارات.',
          },
        },
      ],
    },
    pricing: {
      heading: { fa: 'هزینه چطور حساب می‌شود', en: 'How the cost works', ar: 'كيف تُحسب التكلفة' },
      body: {
        fa: 'هزینه بر اساس منابعی که پروژه مصرف می‌کند حساب می‌شود: پردازنده، حافظه، دیسک و ترافیک، به‌صورت ساعتی. پلن ثابت با سقف پنهان نداریم؛ ترکیب منابع را خودتان انتخاب می‌کنید و فقط همان را می‌پردازید.',
        en: 'Cost follows the resources the project consumes: CPU, memory, disk and traffic, billed hourly. There is no fixed plan with a hidden ceiling; you choose the shape and pay for that.',
        ar: 'تُحسب التكلفة حسب الموارد التي يستهلكها المشروع: المعالج والذاكرة والقرص والحركة، بالساعة. لا خطة ثابتة بسقف خفي؛ تختار التركيبة وتدفع مقابلها.',
      },
      bullets: [
        { fa: 'پرداخت ساعتی، بدون هزینه‌ی راه‌اندازی', en: 'Hourly billing, no setup fee', ar: 'فوترة ساعية بلا رسوم تأسيس' },
        { fa: 'دیتابیس مدیریت‌شده به‌عنوان سرویس جدا و شفاف', en: 'Managed database as a separate, visible line', ar: 'قاعدة بيانات مُدارة كبند منفصل وواضح' },
        { fa: 'تخمین ماهانه پیش از خرید در ماشین‌حساب', en: 'A monthly estimate before you buy', ar: 'تقدير شهري قبل الشراء' },
      ],
      link: { fa: 'محاسبه‌ی هزینه‌ی ماهانه', en: 'Estimate the monthly cost', ar: 'احسب التكلفة الشهرية' },
    },
    migration: {
      heading: { fa: 'مهاجرت از هاست فعلی', en: 'Moving from your current host', ar: 'الانتقال من الاستضافة الحالية' },
      intro: {
        fa: 'بیشترین قطعی مهاجرت از دو جا می‌آید: ایمیل تراکنشی و درگاه پرداخت. این دو را جدا برنامه‌ریزی کنید.',
        en: 'Most migration downtime comes from two places: transactional email and the payment gateway. Plan those two separately.',
        ar: 'معظم انقطاع الانتقال يأتي من أمرين: البريد المعاملاتي وبوابة الدفع. خطط لهما منفصلين.',
      },
      steps: [
        {
          title: { fa: 'بکاپ کامل', en: 'Full backup', ar: 'نسخة كاملة' },
          body: {
            fa: 'دیتابیس را دامپ کنید و storage/app/public را کپی کنید.',
            en: 'Dump the database and copy storage/app/public.',
            ar: 'خذ نسخة من قاعدة البيانات وانسخ storage/app/public.',
          },
        },
        {
          title: { fa: 'کد روی گیت', en: 'Code into git', ar: 'الكود إلى Git' },
          body: {
            fa: 'ریپازیتوری را بدون .env و vendor بالا ببرید.',
            en: 'Push the repository without .env or vendor.',
            ar: 'ارفع المستودع دون .env أو vendor.',
          },
        },
        {
          title: { fa: 'متغیرهای محیطی', en: 'Environment', ar: 'المتغيرات' },
          body: {
            fa: 'APP_URL، SESSION_DOMAIN و اتصال دیتابیس جدید را تنظیم کنید.',
            en: 'Set APP_URL, SESSION_DOMAIN and the new database connection.',
            ar: 'اضبط APP_URL وSESSION_DOMAIN واتصال قاعدة البيانات الجديدة.',
          },
        },
        {
          title: { fa: 'دیپلوی و مهاجرت', en: 'Deploy and migrate', ar: 'انشر واهجر' },
          body: {
            fa: 'migrate --force را اجرا و درگاه پرداخت و ورود کاربران را تست کنید.',
            en: 'Run migrate --force, then test the payment gateway and user login.',
            ar: 'شغّل migrate --force ثم اختبر بوابة الدفع وتسجيل الدخول.',
          },
        },
        {
          title: { fa: 'تغییر DNS', en: 'Switch DNS', ar: 'غيّر DNS' },
          body: {
            fa: 'رکورد دامنه را عوض کنید و تا پایدار شدن، هاست قبلی را خاموش نکنید.',
            en: 'Change the domain record and keep the old host running until the new one is stable.',
            ar: 'غيّر سجل النطاق وأبقِ الاستضافة القديمة حتى يستقر الجديد.',
          },
        },
      ],
    },
    troubleshooting: {
      heading: { fa: 'خطاهای رایج و رفعشان', en: 'Common errors, and the fix', ar: 'الأخطاء الشائعة وحلولها' },
      intro: {
        fa: 'این‌ها خطاهایی هستند که بیشتر از همه در پشتیبانی تکرار می‌شوند.',
        en: 'These are the errors that come up again and again in support.',
        ar: 'هذه أكثر الأخطاء تكرارًا في الدعم.',
      },
      items: [
        {
          problem: { fa: 'بعد از دیپلوی ارور ۵۰۰ می‌گیرم', en: 'A 500 right after deploy', ar: 'خطأ 500 بعد النشر' },
          fix: {
            fa: 'اغلب APP_KEY خالی است یا پوشه‌ی storage اجازه‌ی نوشتن ندارد. کلید را بسازید و مجوز پوشه‌ها را درست کنید.',
            en: 'Usually APP_KEY is empty or storage is not writable. Generate the key and fix the folder permissions.',
            ar: 'غالبًا APP_KEY فارغ أو storage غير قابل للكتابة. أنشئ المفتاح وصحّح صلاحيات المجلدات.',
          },
        },
        {
          problem: { fa: 'جاب‌های صف اجرا نمی‌شوند', en: 'Queued jobs never run', ar: 'مهام الطابور لا تُنفَّذ' },
          fix: {
            fa: 'ورکر بالا نیست یا QUEUE_CONNECTION به درایور اشتباه اشاره می‌کند. پروژه‌ی ورکر را اجرا و درایور را اصلاح کنید.',
            en: 'The worker is down or QUEUE_CONNECTION points at the wrong driver. Start the worker project and fix the driver.',
            ar: 'العامل متوقف أو QUEUE_CONNECTION يشير إلى مشغّل خاطئ. شغّل مشروع العامل وصحّح المشغّل.',
          },
        },
        {
          problem: {
            fa: 'تصاویر آپلودی بعد از هر دیپلوی غیب می‌شوند',
            en: 'Uploaded images vanish after each deploy',
            ar: 'الصور المرفوعة تختفي بعد كل نشر',
          },
          fix: {
            fa: 'فایل‌ها روی دیسک محلی کانتینر ذخیره شده‌اند. FILESYSTEM_DISK را به S3 تغییر دهید.',
            en: 'Files were written to the container’s local disk. Switch FILESYSTEM_DISK to S3.',
            ar: 'كُتبت الملفات على قرص الحاوية المحلي. غيّر FILESYSTEM_DISK إلى S3.',
          },
        },
        {
          problem: { fa: 'ورود کاربران با خطای ۴۱۹ برمی‌گردد', en: 'Login fails with a 419', ar: 'تسجيل الدخول يفشل بخطأ 419' },
          fix: {
            fa: 'دامنه‌ی سشن با دامنه‌ی فعلی نمی‌خواند یا پروکسی‌ها trusted نیستند. SESSION_DOMAIN و trusted proxies را تنظیم کنید.',
            en: 'The session domain no longer matches, or the proxy is not trusted. Set SESSION_DOMAIN and the trusted proxies.',
            ar: 'نطاق الجلسة لا يطابق الحالي أو الوكيل غير موثوق. اضبط SESSION_DOMAIN والوكلاء الموثوقين.',
          },
        },
        {
          problem: {
            fa: 'composer install از ایران قطع می‌شود',
            en: 'composer install stalls from Iran',
            ar: 'composer install ينقطع من إيران',
          },
          fix: {
            fa: 'دسترسی به packagist از داخل ایران پایدار نیست. دیپلوی را در خوشه‌ی آلمان اجرا کنید.',
            en: 'Reaching packagist from inside Iran is not reliable. Run the deploy on the Germany cluster.',
            ar: 'الوصول إلى packagist من داخل إيران غير مستقر. نفّذ النشر في مجموعة ألمانيا.',
          },
        },
        {
          problem: { fa: 'استایل‌ها بعد از دیپلوی قدیمی می‌مانند', en: 'Styles stay stale after a deploy', ar: 'الأنماط تبقى قديمة بعد النشر' },
          fix: {
            fa: 'مرحله‌ی build فرانت اجرا نشده یا کش کانفیگ مانده است. npm run build و config:cache را اضافه کنید.',
            en: 'The front-end build did not run, or the config cache is stale. Add npm run build and config:cache.',
            ar: 'لم تُنفَّذ خطوة بناء الواجهة أو بقي تخزين الإعدادات قديمًا. أضف npm run build وconfig:cache.',
          },
        },
      ],
    },
    faq: {
      heading: { fa: 'پرسش‌های پرتکرار', en: 'Frequent questions', ar: 'الأسئلة الشائعة' },
      items: [
        {
          q: { fa: 'هاست لاراول با هاست اشتراکی چه تفاوتی دارد؟', en: 'How is Laravel hosting different from shared hosting?', ar: 'ما فرق استضافة Laravel عن المشتركة؟' },
          a: {
            fa: 'در هاست اشتراکی PHP فقط برای پاسخ به یک درخواست وب اجرا می‌شود و ورکر دائمی جایی ندارد. هاست لاراول امکان اجرای همیشگی صف، زمان‌بند و دستورهای artisan را می‌دهد.',
            en: 'On shared hosting PHP runs only to answer a request, and a permanent worker has no home. Laravel hosting keeps queues, the scheduler and artisan commands running.',
            ar: 'في المشتركة تعمل PHP لطلب واحد فقط ولا مكان لعامل دائم. استضافة Laravel تبقي الطوابير والمجدول وأوامر artisan تعمل.',
          },
        },
        {
          q: { fa: 'برای اجرای لاراول به SSH نیاز دارم؟', en: 'Do I need SSH to run Laravel?', ar: 'هل أحتاج SSH لتشغيل Laravel؟' },
          a: {
            fa: 'نه. نصب وابستگی و مهاجرت در مرحله‌ی دیپلوی انجام می‌شود. SSH برای دیباگ عمیق هست، نه شرط راه‌اندازی.',
            en: 'No. Dependencies and migrations run during the deploy. SSH is there for deep debugging, not as a setup requirement.',
            ar: 'لا. تُثبَّت التبعيات وتُنفَّذ الهجرات وقت النشر. SSH للتشخيص العميق لا شرطًا للتشغيل.',
          },
        },
        {
          q: { fa: 'صف و ورکر را چطور اجرا کنم؟', en: 'How do I run queues and workers?', ar: 'كيف أشغّل الطوابير والعوامل؟' },
          a: {
            fa: 'صف را روی Redis بگذارید و یک پروژه‌ی Supervisor با دستور queue:work بالا بیاورید. ورکر بعد از خطا خودکار دوباره اجرا می‌شود.',
            en: 'Put the queue on Redis and start a Supervisor project running queue:work. The worker comes back automatically after a crash.',
            ar: 'اجعل الطابور على Redis وشغّل مشروع Supervisor ينفّذ queue:work. يعود العامل تلقائيًا بعد التوقف.',
          },
        },
        {
          q: { fa: 'مهاجرت دیتابیس خودکار است؟', en: 'Are database migrations automatic?', ar: 'هل هجرات قاعدة البيانات تلقائية؟' },
          a: {
            fa: 'اگر دستورش را در مرحله‌ی دیپلوی بگذارید، بله؛ وگرنه دستی از ترمینال اجرا می‌شود.',
            en: 'If you add the command to the deploy step, yes; otherwise you run it by hand from a terminal.',
            ar: 'إن أضفت الأمر إلى خطوة النشر فنعم، وإلا شغّلته يدويًا من الطرفية.',
          },
        },
        {
          q: { fa: 'هزینه چطور محاسبه می‌شود؟', en: 'How is the price calculated?', ar: 'كيف تُحسب التكلفة؟' },
          a: {
            fa: 'ساعتی، بر اساس پردازنده، حافظه، دیسک و ترافیک. ماشین‌حساب عدد ماهانه را نشان می‌دهد.',
            en: 'Hourly, based on CPU, memory, disk and traffic. The calculator shows the monthly figure.',
            ar: 'بالساعة حسب المعالج والذاكرة والقرص والحركة، والحاسبة تعرض الرقم الشهري.',
          },
        },
        {
          q: { fa: 'آیا Reverb و Octane پشتیبانی می‌شوند؟', en: 'Are Reverb and Octane supported?', ar: 'هل Reverb وOctane مدعومان؟' },
          a: {
            fa: 'Reverb روی پورت و دامنه‌ی جدا اجرا می‌شود. برای Octane قبل از مهاجرت با پشتیبانی هماهنگ کنید.',
            en: 'Reverb runs on its own port and domain. For Octane, check with support before you migrate.',
            ar: 'يعمل Reverb على منفذ ونطاق خاصين. أما Octane فتحقق مع الدعم قبل الانتقال.',
          },
        },
        {
          q: { fa: 'مهاجرت از هاست فعلی چقدر طول می‌کشد؟', en: 'How long does the move take?', ar: 'كم يستغرق الانتقال؟' },
          a: {
            fa: 'برای پروژه‌ی معمولی یک روز کاری؛ بیشترین زمان صرف تست درگاه پرداخت و ایمیل می‌شود.',
            en: 'One working day for a normal project. Most of it goes into testing the payment gateway and email.',
            ar: 'يوم عمل واحد لمشروع عادي، ومعظمه في اختبار بوابة الدفع والبريد.',
          },
        },
      ],
    },
    related: {
      heading: { fa: 'فریم‌ورک‌های دیگر', en: 'Other frameworks', ar: 'أطر عمل أخرى' },
      slugs: ['nextjs', 'django', 'nodejs', 'docker'],
    },
    cta: {
      heading: { fa: 'پروژه‌ی لاراول را همین امروز بالا بیاورید', en: 'Get the Laravel project live today', ar: 'أطلق مشروع Laravel اليوم' },
      body: {
        fa: 'ریپازیتوری را وصل کنید، متغیرها را وارد کنید و اولین دیپلوی را ببینید. ساب‌دامین و SSL رایگان از همان ابتدا فعال است.',
        en: 'Connect the repo, set the variables and watch the first deploy. The free subdomain and SSL are there from the start.',
        ar: 'اربط المستودع واضبط المتغيرات وشاهد أول نشر. النطاق الفرعي وSSL جاهزان من البداية.',
      },
      button: { fa: 'شروع دیپلوی لاراول', en: 'Start a Laravel deploy', ar: 'ابدأ نشر Laravel' },
    },
  },
}

/** Slug of every framework page that already has content. */
export const hostingSlugs = Object.keys(hostingPages)

/** Every framework that has, or will have, a hosting page. `active` comes from hostingPages. */
export interface HostingFramework {
  slug: string
  label: string
  blurb: Record<Locale, string>
}

export const hostingFrameworks: HostingFramework[] = [
  {
    slug: 'laravel',
    label: 'Laravel',
    blurb: {
      fa: 'صف، زمان‌بند و ریشه‌ی public؛ بدون cron دستی.',
      en: 'Queues, scheduler and a public document root, without hand-made cron.',
      ar: 'طوابير ومجدول وجذر public، دون cron يدوي.',
    },
  },
  {
    slug: 'nextjs',
    label: 'Next.js',
    blurb: {
      fa: 'بیلد استاتیک یا سرور Node؛ خروجی و ISR.',
      en: 'Static export or a Node server, with ISR where you need it.',
      ar: 'تصدير ثابت أو خادم Node مع ISR عند الحاجة.',
    },
  },
  {
    slug: 'django',
    label: 'Django',
    blurb: {
      fa: 'Gunicorn، فایل‌های استاتیک و مهاجرت دیتابیس.',
      en: 'Gunicorn, static files and database migrations.',
      ar: 'Gunicorn والملفات الثابتة وهجرات قاعدة البيانات.',
    },
  },
  {
    slug: 'nodejs',
    label: 'Node.js',
    blurb: {
      fa: 'API و وب‌سوکت با نسخه‌ی Node قابل انتخاب.',
      en: 'APIs and sockets, on the Node version you pick.',
      ar: 'واجهات API والمقابس، بنسخة Node التي تختارها.',
    },
  },
  {
    slug: 'react',
    label: 'React',
    blurb: {
      fa: 'اپ استاتیک با روتینگ سمت کلاینت.',
      en: 'A static app with client-side routing.',
      ar: 'تطبيق ثابت بتوجيه من جهة العميل.',
    },
  },
  {
    slug: 'vue',
    label: 'Vue / Nuxt',
    blurb: {
      fa: 'SPA یا رندر سمت سرور با Nuxt.',
      en: 'An SPA, or server rendering through Nuxt.',
      ar: 'SPA أو عرض من الخادم عبر Nuxt.',
    },
  },
  {
    slug: 'fastapi',
    label: 'FastAPI',
    blurb: {
      fa: 'سرویس پایتون سبک با Uvicorn.',
      en: 'A light Python service on Uvicorn.',
      ar: 'خدمة Python خفيفة على Uvicorn.',
    },
  },
  {
    slug: 'angular',
    label: 'Angular',
    blurb: {
      fa: 'بیلد تولیدی و سرو استاتیک.',
      en: 'A production build, served statically.',
      ar: 'بناء إنتاجي يُقدَّم بشكل ثابت.',
    },
  },
  {
    slug: 'dotnet',
    label: 'ASP.NET Core',
    blurb: {
      fa: 'انتشار، اجرا و اتصال دیتابیس.',
      en: 'Publish, run and database wiring.',
      ar: 'النشر والتشغيل وربط قاعدة البيانات.',
    },
  },
  {
    slug: 'golang',
    label: 'Go',
    blurb: {
      fa: 'باینری تک‌فایلی، بدون رانتایم اضافه.',
      en: 'A single binary, no extra runtime.',
      ar: 'ملف تنفيذي واحد بلا بيئة إضافية.',
    },
  },
  {
    slug: 'docker',
    label: 'Docker',
    blurb: {
      fa: 'هر چیزی که Dockerfile دارد.',
      en: 'Anything with a Dockerfile.',
      ar: 'أي شيء لديه Dockerfile.',
    },
  },
  {
    slug: 'static',
    label: 'Static HTML',
    blurb: {
      fa: 'سایت ساده، سریع و بی‌هزینه‌ی نگهداری.',
      en: 'A plain site: fast, with nothing to maintain.',
      ar: 'موقع بسيط: سريع بلا صيانة.',
    },
  },
]
